import { Redirect, Route, Switch, useLocation } from "wouter";
import { useAuth } from "./lib/auth-client";
import { useProgressSync } from "./lib/progress";
import { PresenceProvider } from "./lib/presence";
import { LoginScreen } from "./screens/Login";
import { Dashboard } from "./screens/Dashboard";
import { Lesson } from "./screens/Lesson";
import { ConceptLesson } from "./screens/ConceptLesson";
import { ComingSoon } from "./screens/ComingSoon";
import { Admin } from "./screens/Admin";
import { Users } from "./screens/Users";
import { Sandbox } from "./screens/Sandbox";
import { Settings } from "./screens/Settings";
import { ManageCourses } from "./screens/ManageCourses";
import { ManageLectures } from "./screens/ManageLectures";
import { Lecture } from "./screens/Lecture";

export function App() {
  const { user, pending, signOut } = useAuth();
  const [, setLocation] = useLocation();

  // Reconcile localStorage progress with the server for the signed-in learner.
  useProgressSync(user);

  function handleSignOut() {
    signOut();
    setLocation("/login");
  }

  if (pending) return null;

  const routes = (
    <Switch>
      <Route path="/login">
        {user ? <Redirect to="/dashboard" /> : <LoginScreen />}
      </Route>

      <Route path="/dashboard">
        {user ? <Dashboard user={user} onSignOut={handleSignOut} /> : <Redirect to="/login" />}
      </Route>

      <Route path="/sandbox">
        {user ? <Sandbox user={user} onSignOut={handleSignOut} /> : <Redirect to="/login" />}
      </Route>

      <Route path="/admin">
        {!user ? (
          <Redirect to="/login" />
        ) : user.isAdmin ? (
          <Admin user={user} onSignOut={handleSignOut} />
        ) : (
          <Redirect to="/dashboard" />
        )}
      </Route>

      <Route path="/admin/users">
        {!user ? (
          <Redirect to="/login" />
        ) : user.isAdmin ? (
          <Users user={user} onSignOut={handleSignOut} />
        ) : (
          <Redirect to="/dashboard" />
        )}
      </Route>

      <Route path="/settings">
        {user ? <Settings user={user} onSignOut={handleSignOut} /> : <Redirect to="/login" />}
      </Route>

      <Route path="/admin/courses">
        {!user ? (
          <Redirect to="/login" />
        ) : user.isAdmin ? (
          <ManageCourses user={user} onSignOut={handleSignOut} />
        ) : (
          <Redirect to="/dashboard" />
        )}
      </Route>

      <Route path="/admin/lectures">
        {!user ? (
          <Redirect to="/login" />
        ) : user.isAdmin ? (
          <ManageLectures user={user} onSignOut={handleSignOut} />
        ) : (
          <Redirect to="/dashboard" />
        )}
      </Route>

      <Route path="/lecture/:lectureId">
        {(params) => {
          if (!user) return <Redirect to="/login" />;
          return (
            <Lecture
              key={params.lectureId}
              user={user}
              lectureId={params.lectureId}
              onSignOut={handleSignOut}
            />
          );
        }}
      </Route>

      <Route path="/lesson/:courseId">
        {(params) => {
          if (!user) return <Redirect to="/login" />;
          if (
            params.courseId === "commits-branches" ||
            params.courseId === "remote" ||
            params.courseId === "pull-requests" ||
            params.courseId === "merge-conflicts" ||
            params.courseId === "undoing"
          ) {
            return (
              <Lesson
                key={params.courseId}
                user={user}
                courseId={params.courseId}
                onSignOut={handleSignOut}
              />
            );
          }
          if (params.courseId === "foundations") {
            return <ConceptLesson user={user} onSignOut={handleSignOut} />;
          }
          return (
            <ComingSoon user={user} courseId={params.courseId} onSignOut={handleSignOut} />
          );
        }}
      </Route>

      <Route>
        <Redirect to={user ? "/dashboard" : "/login"} />
      </Route>
    </Switch>
  );

  return user ? <PresenceProvider user={user}>{routes}</PresenceProvider> : routes;
}

export default App;
